/**
 * Live progress strip shown at the bottom of the AI chat (above the composer)
 * for any IN_PROGRESS background job — most notably the guest import the
 * assistant just kicked off. Reuses the BackgroundJobsProvider (which already
 * polls job status), so this is a pure read: no extra network, no state of its
 * own. Disappears when the job finishes (the Downloads/Import area then shows
 * the completed result).
 */
import { useTranslations } from "next-intl";
import { motion } from "framer-motion";
import { useBackgroundJobs } from "@/providers/BackgroundJobsProvider";
import type { TrackedJob } from "@/providers/BackgroundJobsProvider";

/** English fallbacks so the strip renders in every locale even before the
 *  aiAssistant.job* keys are translated (t.has guard below). */
const FALLBACK = {
  jobImportingGuests: "Importing guests",
  jobExporting: "Preparing export",
  jobSending: "Sending emails",
  jobTranslating: "Translating",
  jobRunning: "Working…",
} as const;

function labelKey(taskType: TrackedJob["taskType"]): keyof typeof FALLBACK {
  if (taskType.startsWith("IMPORT_GUESTS")) return "jobImportingGuests";
  if (taskType.startsWith("EXPORT")) return "jobExporting";
  if (taskType === "SEND_EMAILS") return "jobSending";
  if (taskType === "TRANSLATE_TEMPLATE") return "jobTranslating";
  return "jobRunning";
}

export function ChatJobProgress() {
  const t = useTranslations("aiAssistant");
  const { jobs } = useBackgroundJobs();
  const active = jobs.filter((j) => j.status === "IN_PROGRESS");
  if (active.length === 0) return null;

  return (
    <div className="shrink-0 px-3 pt-2 flex flex-col gap-1.5" data-testid="ai-chat-job-progress">
      {active.map((job) => {
        const key = labelKey(job.taskType);
        const label = t.has(key) ? t(key) : FALLBACK[key];
        // Guard against a corrupt persisted progress (localStorage) → NaN width.
        const raw = Number.isFinite(job.progress) ? job.progress : 0;
        const pct = Math.min(100, Math.max(0, Math.round(raw)));
        return (
          <div
            key={job.jobId}
            className="rounded-lg border outline-quaternary surface-secondary px-2.5 py-1.5"
          >
            <div className="mb-1 flex items-center justify-between text-xs font-medium">
              <span className="truncate">{label}</span>
              <span className="font-tertiary tabular-nums">{pct}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full surface-tertiary">
              <motion.div
                className="h-full cta-primary"
                initial={false}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
